import { caloTheme } from 'src/theme/calo';
import { useState } from 'react';

import {
  Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';

import CaloLoader from '../../../components/CaloLoader';
import CreditTransactionRow from './CreditTransactionRow';
import DebitTransactionRow from './DebitTransactionRow';
import { Currency } from '../../../lib/enum';
import {
  AffiliateCreditTransactionItem,
  AffiliateDebitTransactionItem,
  PaginationInfo
} from '../../../lib/types';

interface TransactionsTableProps {
  creditTransactions: AffiliateCreditTransactionItem[];
  debitTransactions: AffiliateDebitTransactionItem[];
  creditLoading: boolean;
  debitLoading: boolean;
  creditPagination: PaginationInfo | null;
  debitPagination: PaginationInfo | null;
  affiliateCurrency?: Currency;
  onPageChange: (pageNumber: number, isCredit: boolean) => void;
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    border: 'none'
  },
  [`&.${tableCellClasses.body}`]: {
    border: 'none',
    justifyContent: 'space-between'
  }
}));

const TransactionsTable = ({
  creditTransactions,
  debitTransactions,
  creditLoading,
  debitLoading,
  creditPagination,
  debitPagination,
  affiliateCurrency,
  onPageChange
}: TransactionsTableProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const isCreditTab = activeTab === 0;
  const currentTransactions = isCreditTab ? creditTransactions : debitTransactions;
  const isLoading = isCreditTab ? creditLoading : debitLoading;
  const currentPagination = isCreditTab ? creditPagination : debitPagination;

  const renderCreditTableHeaders = () => (
    <>
      <StyledTableCell
        sx={{
          borderRadius: '8px 0 0 8px',
          whiteSpace: 'nowrap',
          fontSize: { xs: '12px', sm: '14px' }
        }}
      >
        Credit Reason
      </StyledTableCell>
      <StyledTableCell sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Status
      </StyledTableCell>
      <StyledTableCell sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Transaction Date
      </StyledTableCell>
      <StyledTableCell
        sx={{
          borderRadius: '0 8px 8px 0',
          fontSize: { xs: '12px', sm: '14px' }
        }}
      >
        Amount
      </StyledTableCell>
    </>
  );

  const renderDebitTableHeaders = () => (
    <>
      <StyledTableCell
        sx={{
          borderRadius: '8px 0 0 8px',
          whiteSpace: 'nowrap',
          fontSize: { xs: '12px', sm: '14px' }
        }}
      >
        Transaction Type
      </StyledTableCell>
      <StyledTableCell sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Type
      </StyledTableCell>
      <StyledTableCell sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Transaction Ref
      </StyledTableCell>
      <StyledTableCell sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Transaction Date
      </StyledTableCell>
      <StyledTableCell
        sx={{
          borderRadius: '0 8px 8px 0',
          fontSize: { xs: '12px', sm: '14px' }
        }}
      >
        Amount
      </StyledTableCell>
    </>
  );

  return (
    <>
      <Box
        sx={{
          padding: { xs: 1, sm: 2 },
          backgroundColor: 'white',
          borderRadius: '8px',
          overflowX: 'auto'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            mb: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '16px'
            }
          }}
        >
          <Tab label="Credit Transactions" />
          <Tab label="Debit Transactions" />
        </Tabs>

        {isLoading ? (
          <Stack sx={{ width: '100%', justifyContent: 'center' }}>
            <CaloLoader />
          </Stack>
        ) : (
          <Table
            sx={{
              marginY: '4px',
              minWidth: { xs: '800px', md: '100%' }
            }}
          >
            <TableHead
              sx={{
                backgroundColor: caloTheme.palette.neutral50,
                borderRadius: '8px'
              }}
            >
              <TableRow>
                {isCreditTab ? renderCreditTableHeaders() : renderDebitTableHeaders()}
              </TableRow>
            </TableHead>
            {currentTransactions?.length > 0 ? (
              <TableBody>
                {isCreditTab
                  ? currentTransactions.map((transaction) => (
                    <CreditTransactionRow
                      transaction={transaction}
                      currency={affiliateCurrency}
                      key={transaction.id}
                    />
                  ))
                  : currentTransactions.map((transaction) => (
                    <DebitTransactionRow
                      transaction={transaction}
                      currency={affiliateCurrency}
                      key={transaction.id}
                    />
                  ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={isCreditTab ? 4 : 5}>
                    <Typography align="center" sx={{ py: 2 }}>
                      NO TRANSACTIONS!
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        )}
      </Box>
      {currentPagination && currentPagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ mt: 2 }}>
          <Button
            disabled={currentPagination.pageNumber === 1}
            onClick={() => onPageChange(currentPagination.pageNumber - 1, isCreditTab)}
            sx={{ width: { xs: 'auto', sm: 'auto' } }}
          >
            Previous
          </Button>
          <Typography>
            Page {currentPagination.pageNumber} of {currentPagination.totalPages}
          </Typography>
          <Button
            disabled={currentPagination.pageNumber >= currentPagination.totalPages}
            onClick={() => onPageChange(currentPagination.pageNumber + 1, isCreditTab)}
            sx={{ width: { xs: 'auto', sm: 'auto' } }}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
};

export default TransactionsTable;
